/**
 * Feedback dialog for the /reranker tool, opened from the shared FeedbackSideTab.
 *
 * 'rate' asks one question (is this ranking right for the customer?) plus a
 * comment; 'report' asks only for the comment. The submission carries a
 * snapshot of the ranking on screen so it stays meaningful after the cached
 * result expires.
 */

import { Check, Loader2, X } from 'lucide-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type AffinityDocument,
  type AffinityRankingAnswer,
  submitAffinityFeedback,
} from '../services/api';

const RANKING_ANSWERS: AffinityRankingAnswer[] = ['good', 'mixed', 'poor'];
const SNAPSHOT_SIZE = 50;

interface AffinityFeedbackModalProps {
  mode: 'rate' | 'report';
  shopifyCustomerId: string;
  document: AffinityDocument | null;
  topK?: number;
  onClose: () => void;
}

function useDialogBehaviour(
  dialogRef: React.RefObject<HTMLDivElement | null>,
  onClose: () => void
) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector<HTMLElement>('button, textarea')?.focus();
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [dialogRef, onClose]);

  return (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'button, textarea, [href], input, select'
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && window.document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && window.document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
}

const AffinityFeedbackModal: React.FC<AffinityFeedbackModalProps> = ({
  mode,
  shopifyCustomerId,
  document: result,
  topK,
  onClose,
}) => {
  const { t } = useTranslation();
  const [ranking, setRanking] = useState<AffinityRankingAnswer | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const trapFocus = useDialogBehaviour(dialogRef, onClose);

  const isReport = mode === 'report';
  const hasComment = comment.trim().length > 0;
  const canSubmit = isReport ? hasComment : ranking !== null || hasComment;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await submitAffinityFeedback({
        kind: mode,
        shopify_customer_id: shopifyCustomerId,
        request_key: result?.request_key ?? null,
        ranking: isReport ? null : ranking,
        comment,
        mode: result?.mode ?? null,
        top_k: result?.mode === 'retrieve' ? (topK ?? null) : null,
        scorer: result?.scorer ?? null,
        cf_version: result?.cf_version ?? null,
        top_book_ids: (result?.books ?? []).slice(0, SNAPSHOT_SIZE).map((row) => row.book_id),
      });
      setIsDone(true);
    } catch {
      setError(t('affinity.feedback.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = isDone
    ? t('affinity.feedback.doneTitle')
    : t(isReport ? 'affinity.feedback.reportTitle' : 'affinity.feedback.title');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="affinity-feedback-title"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={trapFocus}
        className="w-full max-w-md border border-indigo-brand-border bg-white shadow-xl"
      >
        <div className="flex items-start justify-between border-b border-indigo-brand-border px-5 py-4">
          <h2
            id="affinity-feedback-title"
            className="font-heading text-lg font-bold text-indigo-brand-text"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="text-indigo-brand-text/50 hover:text-indigo-brand-text"
          >
            <X size={18} />
          </button>
        </div>

        {isDone ? (
          <div className="px-5 py-8 text-center">
            <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-indigo-brand-off-white">
              <Check size={20} className="text-indigo-brand-black" />
            </span>
            <p className="text-sm text-indigo-brand-text/70">{t('affinity.feedback.doneBody')}</p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 w-full bg-indigo-brand-black px-4 py-2.5 text-sm font-medium text-white hover:bg-black"
            >
              {t('affinity.feedback.doneCta')}
            </button>
          </div>
        ) : (
          <div className="px-5 py-5">
            {isReport ? null : (
              <fieldset className="mb-5">
                <legend className="mb-2 text-sm font-medium text-indigo-brand-text">
                  {t('affinity.feedback.rankingQuestion')}
                </legend>
                <div className="flex gap-2">
                  {RANKING_ANSWERS.map((answer) => (
                    <button
                      key={answer}
                      type="button"
                      aria-pressed={ranking === answer}
                      onClick={() => setRanking(answer)}
                      className={`flex-1 border px-3 py-2 text-sm transition-colors ${
                        ranking === answer
                          ? 'border-indigo-brand-black bg-indigo-brand-black text-white'
                          : 'border-indigo-brand-border bg-white text-indigo-brand-text hover:bg-indigo-brand-off-white'
                      }`}
                    >
                      {t(`affinity.feedback.rankingScale.${answer}`)}
                    </button>
                  ))}
                </div>
              </fieldset>
            )}

            <label
              htmlFor="affinity-feedback-comment"
              className="mb-2 block text-sm font-medium text-indigo-brand-text"
            >
              {t(isReport ? 'affinity.feedback.reportLabel' : 'affinity.feedback.commentLabel')}
            </label>
            <textarea
              id="affinity-feedback-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              rows={isReport ? 5 : 3}
              maxLength={4000}
              placeholder={t(
                isReport
                  ? 'affinity.feedback.reportPlaceholder'
                  : 'affinity.feedback.commentPlaceholder'
              )}
              className="w-full border border-indigo-brand-border px-3 py-2 text-sm focus:border-indigo-brand-black focus:outline-none"
            />

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
              className={`mt-5 flex w-full items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                canSubmit && !isSubmitting
                  ? 'bg-indigo-brand-black text-white hover:bg-black'
                  : 'cursor-not-allowed bg-indigo-brand-border text-white'
              }`}
            >
              {isSubmitting && <Loader2 size={16} className="animate-spin" />}
              {t(isSubmitting ? 'affinity.feedback.submitting' : 'affinity.feedback.submit')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffinityFeedbackModal;
